import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../../utils/numberFormatting';
import client from '../../../client';
import { useMessage } from 'app/shared-components/MessageUtils';

const ModalEditTransaction = ({ 
  open, 
  setOpen, 
  onUpdate, 
  selectedTransaction, 
  driverData, 
  truckData,
  compacctnum 
}) => {
  const { t } = useTranslation('navigation');
  const showMessage = useMessage();
  const [formData, setFormData] = useState({
    quantity: '',
    driver: '',
    driverId: '',
    truck: '',
    truckId: '',
    mileage: '',
    notes: '',
    inventoryMeasure: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // Check if the transaction is an Adjustment Transaction
  const isAdjustmentTransaction = selectedTransaction && 
    selectedTransaction.driver === 'Adjustment Transaction' && 
    selectedTransaction.truck === 'Adjustment Transaction' && 
    selectedTransaction.mileage === 'Adjustment Transaction' && 
    selectedTransaction.notes === 'Adjustment Transaction';

  // Initialize form data when transaction is selected
  useEffect(() => {
    if (selectedTransaction) {
      setFormData({
        quantity: selectedTransaction.fulltransactionQty || selectedTransaction.totalQuantity || '',
        driver: selectedTransaction.driver || '',
        driverId: selectedTransaction.driver_id || '',
        truck: selectedTransaction.truck || '',
        truckId: selectedTransaction.truck_id || '',
        mileage: selectedTransaction.mileage || '',
        notes: selectedTransaction.notes || '',
        inventoryMeasure: selectedTransaction.inventory_measure || ''
      });
    }
  }, [selectedTransaction]);

  const handleClose = () => {
    setOpen(false);
    setFormData({
      quantity: '',
      driver: '',
      driverId: '',
      truck: '',
      truckId: '',
      mileage: '',
      notes: '',
      inventoryMeasure: ''
    });
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.quantity || formData.quantity <= 0) {
      newErrors.quantity = t('QUANTITY_REQUIRED');
    }
    
    // Only validate driver, truck, and mileage if it's not an Adjustment Transaction
    if (!isAdjustmentTransaction) {
      if (!formData.driver || formData.driver.trim() === '') {
        newErrors.driver = t('DRIVER_REQUIRED');
      }
      
      if (!formData.truck || formData.truck.trim() === '') {
        newErrors.truck = t('TRUCK_REQUIRED');
      }
      
      if (!formData.mileage || formData.mileage <= 0) {
        newErrors.mileage = t('MILEAGE_REQUIRED');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      // Check if at least one field has been modified
      const hasChanges = 
        formData.quantity !== (selectedTransaction.totalQuantity || '') ||
        formData.driverId !== (selectedTransaction.driver_id || '') ||
        formData.truckId !== (selectedTransaction.truck_id || '') ||
        formData.mileage !== (selectedTransaction.mileage || '') ||
        formData.notes !== (selectedTransaction.notes || '') ||
        formData.inventoryMeasure !== (selectedTransaction.inventoryMeasure || '');

      if (!hasChanges) {
        showMessage(t('AT_LEAST_ONE_FIELD_MUST_BE_MODIFIED'), 'warning');
        return;
      }

      setLoading(true);
      try {
        const response = await client.put(`/fuel-inventory/update-transaction/${compacctnum}`, {
          transaction_id: selectedTransaction.transaction_id,
          dbId: selectedTransaction.dbId,
          quantity: formData.quantity,
          driver: formData.driverId,
          truck: formData.truckId,
          mileage: formData.mileage,
          notes: formData.notes,
          inventoryMeasure: formData.inventoryMeasure
        });
        
        if (response && response.data) {
          // Show message from backend if available, otherwise show default success message
          const backendMessage = response.data.message || response.data.msg || response.data.error;
          if (backendMessage) {
            showMessage(backendMessage, 'success');
          } else {
            showMessage(t('TRANSACTION_UPDATED_SUCCESSFULLY'), 'success');
          }
          
          setOpen(false);
          setFormData({
            quantity: '',
            driver: '',
            driverId: '',
            truck: '',
            truckId: '',
            mileage: '',
            notes: '',
            inventoryMeasure: ''
          });
          setErrors({});
          // Call the onUpdate callback to refresh the parent component
          if (onUpdate) {
            onUpdate();
          }
        }
      } catch (e) {
        // Show error message from backend if available, otherwise show default error message
        const errorMessage = e.response?.data?.message || e.response?.data?.msg || e.response?.data?.error;
        if (errorMessage) {
          showMessage(errorMessage, 'error');
        } else {
          showMessage(t('FAILED_UPDATE_TRANSACTION'), 'error');
        }
      }
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    
    // If driver name changes, also clear driverId to force re-selection
    if (field === 'driver') {
      setFormData(prev => ({
        ...prev,
        driverId: ''
      }));
    }
    
    // If truck number changes, also clear truckId to force re-selection
    if (field === 'truck') {
      setFormData(prev => ({
        ...prev,
        truckId: ''
      }));
    }
  };

  const handleDelete = () => {
    if (!isAdjustmentTransaction) {
      return;
    }
    // Show confirmation modal
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteConfirmation(false);
    setLoading(true);
    try {
      const response = await client.delete(`/fuel-inventory/delete-adjustment-transaction/${compacctnum}`, {
        data: {
          transaction_id: selectedTransaction.transaction_id,
          dbId: selectedTransaction.dbId
        }
      });
      
      if (response && response.data) {
        // Show message from backend if available, otherwise show default success message
        const backendMessage = response.data.message || response.data.msg || response.data.error;
        if (backendMessage) {
          showMessage(backendMessage, 'success');
        } else {
          showMessage(t('TRANSACTION_DELETED_SUCCESSFULLY'), 'success');
        }
        
        setOpen(false);
        setFormData({
          quantity: '',
          driver: '',
          driverId: '',
          truck: '',
          truckId: '',
          mileage: '',
          notes: '',
          inventoryMeasure: ''
        });
        setErrors({});
        // Call the onUpdate callback to refresh the parent component
        if (onUpdate) {
          onUpdate();
        }
      }
    } catch (e) {
      // Show error message from backend if available, otherwise show default error message
      const errorMessage = e.response?.data?.message || e.response?.data?.msg || e.response?.data?.error;
      if (errorMessage) {
        showMessage(errorMessage, 'error');
      } else {
        showMessage(t('FAILED_DELETE_TRANSACTION'), 'error');
      }
    }
    setLoading(false);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  return (
    <>
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ textAlign: 'center' }}>
        <Typography variant="h6" component="div">
          {t('EDIT_TRANSACTION')}
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Row 1: Transaction ID and Fuel/Lube Quantity */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={t('TRANSACTION_ID')}
              value={selectedTransaction?.transaction_id || ''}
              disabled
              inputProps={{
                style: {
                  paddingTop: '7.5px',
                  paddingBottom: '7.5px',
                }
              }}
              sx={{ 
                flex: 1,
                '& .MuiInputLabel-root': {
                  fontSize: '18px',
                  fontWeight: '500',
                  color: 'black',
                  transform: 'translate(14px, -18px) scale(0.75)',
                  '&.Mui-focused': {
                    transform: 'translate(14px, -18px) scale(0.75)',
                    color: 'black'
                  },
                  '&.MuiFormLabel-filled': {
                    transform: 'translate(14px, -18px) scale(0.75)',
                    color: 'black'
                  }
                }
              }}
              variant="outlined"
            />
            <TextField
              label={t('FUEL_LUBE_QUANTITY')}
              type="number"
              value={formData.quantity}
              onChange={(e) => handleInputChange('quantity', parseFloat(e.target.value) || '')}
              error={!!errors.quantity}
              helperText={errors.quantity}
              disabled={isAdjustmentTransaction}
              sx={{ 
                flex: 1,
                '& .MuiInputLabel-root': {
                  fontSize: '18px',
                  fontWeight: '500',
                  color: 'black',
                  transform: 'translate(14px, -18px) scale(0.75)',
                  '&.Mui-focused': {
                    transform: 'translate(14px, -18px) scale(0.75)',
                    color: 'black'
                  },
                  '&.MuiFormLabel-filled': {
                    transform: 'translate(14px, -18px) scale(0.75)',
                    color: 'black'
                  }
                }
              }}
              variant="outlined"
              inputProps={{ step: "0.001", min: "0", style: { paddingTop: '7.5px', paddingBottom: '7.5px' } }}
            />
          </Box>

          {/* Inventory Measure - Only visible for Adjustment Transaction */}
          {isAdjustmentTransaction && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label={t('INVENTORY_MEASURE')}
                value={formData.inventoryMeasure}
                onChange={(e) => handleInputChange('inventoryMeasure', e.target.value)}
                variant="outlined"
                disabled={isAdjustmentTransaction}
                inputProps={{
                  style: {
                    paddingTop: '7.5px',
                    paddingBottom: '7.5px',
                  }
                }}
                sx={{ 
                  flex: 1,
                  '& .MuiInputLabel-root': {
                    fontSize: '18px',
                    fontWeight: '500',
                    color: 'black',
                    transform: 'translate(14px, -18px) scale(0.75)',
                    '&.Mui-focused': {
                      transform: 'translate(14px, -18px) scale(0.75)',
                      color: 'black'
                    },
                    '&.MuiFormLabel-filled': {
                      transform: 'translate(14px, -18px) scale(0.75)',
                      color: 'black'
                    }
                  }
                }}
              />
              <Box sx={{ flex: 1 }} />
            </Box>
          )}

          {/* Row 2: Driver and Truck - Hidden for Adjustment Transaction */}
          {!isAdjustmentTransaction && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Autocomplete
                options={driverData}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  if (option && option.name) return option.name;
                  if (option && option.user_fname && option.user_lname) {
                    return `${option.user_fname} ${option.user_lname}`;
                  }
                  return '';
                }}
                value={driverData.find(driver => 
                  driver.name === formData.driver || 
                  (driver.user_fname && driver.user_lname && `${driver.user_fname} ${driver.user_lname}` === formData.driver)
                ) || formData.driver}
                onChange={(event, value) => {
                  const driverName = typeof value === 'string' ? value : 
                    (value?.name || (value?.user_fname && value?.user_lname ? `${value.user_fname} ${value.user_lname}` : ''));
                  const driverId = typeof value === 'string' ? '' : (value?.dr_user_id || '');
                  handleInputChange('driver', driverName);
                  handleInputChange('driverId', driverId);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('DRIVER')}
                    error={!!errors.driver}
                    helperText={errors.driver}
                    variant="outlined"
                    sx={{
                      '& .MuiInputLabel-root': {
                        fontSize: '18px',
                        fontWeight: '500',
                        color: 'black',
                        transform: 'translate(14px, -18px) scale(0.75)',
                        '&.Mui-focused': {
                          transform: 'translate(14px, -18px) scale(0.75)',
                          color: 'black'
                        },
                        '&.MuiFormLabel-filled': {
                          transform: 'translate(14px, -18px) scale(0.75)',
                          color: 'black'
                        }
                      }
                    }}
                  />
                )}
                freeSolo
                sx={{ flex: 1 }}
              />
              <Autocomplete
                options={truckData}
                getOptionLabel={(option) => option ? `No. ${option.t_no_truck}` : ''}
                value={truckData.find(truck => 'No: '+truck.t_no_truck == formData.truck) || null}
                onChange={(event, value) => {
                  const truckNumber = value?.t_no_truck || '';
                  const truckId = value?.idx_trucks_p || '';
                  handleInputChange('truck', truckNumber);
                  handleInputChange('truckId', truckId);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('TRUCK')}
                    error={!!errors.truck}
                    helperText={errors.truck}
                    variant="outlined"
                    sx={{
                      '& .MuiInputLabel-root': {
                        fontSize: '18px',
                        fontWeight: '500',
                        color: 'black',
                        transform: 'translate(14px, -18px) scale(0.75)',
                        '&.Mui-focused': {
                          transform: 'translate(14px, -18px) scale(0.75)',
                          color: 'black'
                        },
                        '&.MuiFormLabel-filled': {
                          transform: 'translate(14px, -18px) scale(0.75)',
                          color: 'black'
                        }
                      }
                    }}
                  />
                )}
                sx={{ flex: 1 }}
              />
            </Box>
          )}

          {/* Row 3: Mileage - Hidden for Adjustment Transaction */}
          {!isAdjustmentTransaction && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label={t('MILEAGE')}
                type="number"
                value={formData.mileage}
                onChange={(e) => handleInputChange('mileage', parseFloat(e.target.value) || '')}
                error={!!errors.mileage}
                helperText={errors.mileage}
                sx={{ 
                  flex: 1,
                  '& .MuiInputLabel-root': {
                    fontSize: '18px',
                    fontWeight: '500',
                    color: 'black',
                    transform: 'translate(14px, -18px) scale(0.75)',
                    '&.Mui-focused': {
                      transform: 'translate(14px, -18px) scale(0.75)',
                      color: 'black'
                    },
                    '&.MuiFormLabel-filled': {
                      transform: 'translate(14px, -18px) scale(0.75)',
                      color: 'black'
                    }
                  }
                }}
                variant="outlined"
                inputProps={{ step: "0.1", min: "0", style: { paddingTop: '7.5px', paddingBottom: '7.5px' } }}
              />
              <Box sx={{ flex: 1 }} />
            </Box>
          )}

          {/* Row 4: Notes - Full Width - Hidden for Adjustment Transaction */}
          {!isAdjustmentTransaction && (
            <TextField
              label={t('NOTES')}
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              placeholder={t('ENTER_NOTES_OPTIONAL')}
              sx={{
                '& .MuiInputLabel-root': {
                  fontSize: '18px',
                  fontWeight: '500',
                  color: 'black',
                  transform: 'translate(14px, -18px) scale(0.75)',
                  '&.Mui-focused': {
                    transform: 'translate(14px, -18px) scale(0.75)',
                    color: 'black'
                  },
                  '&.MuiFormLabel-filled': {
                    transform: 'translate(14px, -18px) scale(0.75)',
                    color: 'black'
                  }
                }
              }}
            />
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          {t('CANCEL')}
        </Button>
        {isAdjustmentTransaction && (
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            {loading ? t('DELETING') : t('DELETE')}
          </Button>
        )}
        {!isAdjustmentTransaction && (
          <Button 
            onClick={handleSubmit} 
            color="primary" 
            variant="contained"
            disabled={loading}
          >
            {loading ? t('UPDATING') : t('UPDATE_TRANSACTION')}
          </Button>
        )}
      </DialogActions>
    </Dialog>

    {/* Confirmation Modal for Delete */}
    <Dialog
      open={showDeleteConfirmation}
      onClose={handleCancelDelete}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ textAlign: 'center' }}>
        <Typography variant="h6" component="div" color="error">
          {t('DELETE_ADJUSTMENT_TRANSACTION')}
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('DELETE_ADJUSTMENT_WARNING')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('DELETE_ADJUSTMENT_NO_UNDO')}
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleCancelDelete} color="secondary">
          {t('CANCEL')}
        </Button>
        <Button 
          onClick={handleConfirmDelete} 
          color="error" 
          variant="contained"
        >
          {t('CONFIRM_DELETE')}
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
};

export default ModalEditTransaction;
